import { Link, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ArrowRight, Calendar, User, Clock } from "lucide-react";
import type { BlogPost as BlogPostType } from "@shared/schema";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-features">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-pricing">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="link-docs">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-foreground font-medium" data-testid="link-blog">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" data-testid="button-login">
              Login
            </Button>
          </Link>
          <Link href="/dashboard">
            <Button size="sm" data-testid="button-signup">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-features">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-pricing">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-docs">
              Docs
            </Link>
            <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-blog">
              Blog
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-contact">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-privacy">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-terms">
              Terms
            </Link>
            <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground" data-testid="footer-link-login">
              Login
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

function formatDate(date: Date | string | null) {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function estimateReadTime(content: string): number {
  const wordsPerMinute = 200;
  const words = content.split(/\s+/).length;
  return Math.max(1, Math.ceil(words / wordsPerMinute));
}

function renderContent(content: string) {
  const paragraphs = content.split("\n\n").filter(Boolean);
  
  return paragraphs.map((paragraph, index) => {
    if (paragraph.startsWith("## ")) {
      return (
        <h2 key={index} className="text-2xl font-semibold mt-8 mb-4">
          {paragraph.slice(3)}
        </h2>
      );
    }
    if (paragraph.startsWith("### ")) {
      return (
        <h3 key={index} className="text-xl font-semibold mt-6 mb-3">
          {paragraph.slice(4)}
        </h3>
      );
    }
    if (paragraph.startsWith("- ") || paragraph.startsWith("* ")) {
      const items = paragraph.split("\n").map((line) => line.replace(/^[-*]\s*/, ""));
      return (
        <ul key={index} className="list-disc list-inside space-y-2 my-4 text-muted-foreground">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }
    if (paragraph.match(/^\d+\.\s/)) {
      const items = paragraph.split("\n").map((line) => line.replace(/^\d+\.\s*/, ""));
      return (
        <ol key={index} className="list-decimal list-inside space-y-2 my-4 text-muted-foreground">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    }
    if (paragraph.startsWith("> ")) {
      return (
        <blockquote key={index} className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground my-6">
          {paragraph.slice(2)}
        </blockquote>
      );
    }
    return (
      <p key={index} className="text-muted-foreground leading-relaxed my-4">
        {paragraph}
      </p>
    );
  });
}

function BlogPostSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <Skeleton className="h-8 w-24 mb-8" />
      <Skeleton className="h-12 w-full mb-4" />
      <Skeleton className="h-12 w-3/4 mb-6" />
      <div className="flex gap-4 mb-8">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-80 w-full mb-8 rounded-lg" />
      <div className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery<BlogPostType>({
    queryKey: ["/api/blog", slug],
  });

  const { data: allPosts } = useQuery<BlogPostType[]>({
    queryKey: ["/api/blog"],
  });

  const relatedPosts = allPosts?.filter((p) => p.slug !== slug).slice(0, 3) || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <BlogPostSkeleton />
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-semibold mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Link href="/blog">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main>
        <article className="py-16">
          <div className="max-w-3xl mx-auto px-6">
            <Link href="/blog">
              <Button variant="ghost" size="sm" className="gap-2 mb-8" data-testid="button-back-to-blog">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Button>
            </Link>

            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {post.tags?.map((tag) => (
                  <Badge key={tag} variant="outline">{tag}</Badge>
                ))}
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6" data-testid="text-post-title">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {estimateReadTime(post.content)} min read
                </span>
              </div>
            </header>

            {post.heroImageUrl && (
              <div className="mb-8 rounded-lg overflow-hidden">
                <img 
                  src={post.heroImageUrl} 
                  alt={post.title}
                  className="w-full h-auto"
                  data-testid="img-post-hero"
                />
              </div>
            )}

            <div className="prose prose-lg max-w-none" data-testid="content-post-body">
              {renderContent(post.content)}
            </div>
          </div>
        </article>

        {relatedPosts.length > 0 && (
          <section className="py-16 bg-muted/30">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-semibold mb-8 text-center">More Articles</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link key={relatedPost.id} href={`/blog/${relatedPost.slug}`}>
                    <div className="group cursor-pointer" data-testid={`card-related-post-${relatedPost.id}`}>
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">
              Ready to grow your online reputation?
            </h2>
            <p className="text-muted-foreground mb-6">
              Start collecting more Google reviews today with ReviewKit.
            </p>
            <Link href="/dashboard">
              <Button size="lg" className="gap-2" data-testid="button-post-cta">
                Get Started Free
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
